import Cookies from 'js-cookie';

/**
 * Isolated legacy compatibility adapter (T017).
 *
 * The current (pre-#3888) authentication mechanism stores the Bearer access token in the
 * `access_token` cookie via `js-cookie` — set in `src/views/user/LoginPage.tsx` and
 * `src/redux/sagas/auth.saga.jsx`, read by `src/helpers/auth-header.jsx`'s `getAuthToken()` (which
 * `src/shared/apollo.ts` and other request clients already depend on), and cleared in several
 * places (`src/shared/utils.ts`, `src/modules/system/utils.ts`,
 * `src/services/clients/insightt-api-client.jsx`).
 *
 * This module reads the cookie itself, the same one-line read as `getAuthToken()`, rather than
 * importing `auth-header.jsx`: that helper imports this module's public entry, and the import would
 * close a cycle through `auth-boundary.ts` (T094). It does not modify `auth-header.jsx` or any of its
 * current consumers; wiring a real
 * consumer onto this adapter (or onto `auth-boundary.ts`) is later tasks' job (T029). Its sole
 * purpose here is to expose the legacy session through the same narrow shape
 * (`LegacySessionAdapter`) that `auth-boundary.ts` composes alongside the SSO in-memory credential
 * envelope, so the boundary's public facade (`getAccessToken()`, `isAuthenticated()`,
 * `getAuthMode()`, `cleanup()`) never has to branch on authentication mode in a caller-visible way
 * (constitution: "no auth-mode branch in feature modules" — any such branch lives only inside this
 * adapter/the boundary's internals).
 *
 * The legacy cookie is intentionally read fresh on every call (never cached in a module-level
 * variable): a legacy sign-in/sign-out can happen outside this module today (existing login page,
 * sagas, and request-client interceptors all still own writing/clearing the cookie directly until
 * T029 rewires them), so caching here could observe a stale value.
 *
 * ## Downgrade protection is the caller's job, not this module's (T033; FR-018, FR-019, FR-020)
 *
 * This adapter deliberately stays a dumb, generation-unaware cookie mirror: it has no store/
 * boundary dependency (see the module-load-order note further down for one concrete reason a
 * dependency loop here would be dangerous), so it cannot itself know whether the cookie it is
 * reporting belongs to the Dashboard session generation the auth boundary currently considers
 * authoritative.
 *
 * That is precisely the gap `auth-boundary.ts` closes: because this cookie predates the boundary
 * and is still written/cleared by code paths entirely outside it (the login page, sagas, request-
 * client interceptors), a cookie that reappears or persists AFTER the boundary has already run an
 * authoritative `cleanup()` — logout, unauthorized handling, or an SSO-side sign-out — must not
 * silently "downgrade" an already-terminated (or already-SSO) session back into an inferred LEGACY
 * one. `auth-boundary.ts`'s `getAccessToken()`/`getAuthMode()` therefore never call `getAccessToken()`/
 * `hasSession()` below unconditionally — they first check `isLegacyEstablishmentCurrentlyAuthorized()`
 * (see that module's doc comment), which trusts this adapter's raw cookie state only for (a) the
 * inactive baseline — a boundary instance that has never run a cleanup/logout transaction at all,
 * preserving pre-existing behavior unchanged per FR-020 — or (b) a generation the boundary was
 * explicitly told established a legacy session via `establishLegacySession()`, tying establishment to
 * the CURRENT generation rather than letting a stale/superseded sign-in's cookie write count as one.
 * `clear()` below is likewise called as part of that same cleanup transaction (see `runCleanup`), so
 * termination and the generation bump that makes it authoritative land together.
 *
 * This module's own contract is therefore unchanged by T033: it still just mirrors the cookie,
 * verbatim, on every call. Do not add generation/session awareness here — that would duplicate the
 * one authoritative generation counter this feature already maintains in the `auth` Redux slice
 * (`session.generation`), which `auth-boundary.ts` reads directly.
 */
export interface LegacySessionAdapter {
  /**
   * The current legacy Bearer access token, or `null` when no legacy cookie session exists.
   * Reports the raw cookie unconditionally — callers MUST gate this behind the boundary's
   * generation-authorization check (T033) rather than trusting it directly; see this module's
   * doc comment.
   */
  getAccessToken(): string | null;
  /**
   * True when a legacy cookie session is currently present. Same downgrade caveat as
   * {@link getAccessToken} — this is a raw presence check, not an authorization decision.
   */
  hasSession(): boolean;
  /** Clears the legacy cookie session. Idempotent — clearing an absent session is a no-op. */
  clear(): void;
}

/** The `js-cookie` key the current legacy mechanism stores the Bearer access token under. */
export const LEGACY_ACCESS_TOKEN_COOKIE = 'access_token';

/** Creates a {@link LegacySessionAdapter} bound to the real `access_token` cookie. */
export function createLegacySessionAdapter(): LegacySessionAdapter {
  return {
    getAccessToken: () => Cookies.get(LEGACY_ACCESS_TOKEN_COOKIE) ?? null,

    hasSession: () => Boolean(Cookies.get(LEGACY_ACCESS_TOKEN_COOKIE)),

    clear: () => {
      Cookies.remove(LEGACY_ACCESS_TOKEN_COOKIE);
    },
  };
}
